import axios, { AxiosError } from "axios";
import type { InternalAxiosRequestConfig } from "axios";
import { useAuthStore } from "../store/AuthStore";

// ----------------------------------------------------------------------

// An explicit VITE_API_BASE_URL env var always wins over auto-detection.
const resolveApiBase = (): string => {
	const fromEnv = import.meta.env.VITE_API_BASE_URL as string | undefined
	if (fromEnv) return fromEnv

	if (typeof window !== 'undefined') {
		const host = window.location.hostname
		// Local dev
		if (host === 'localhost' || host === '127.0.0.1') {
			return 'http://localhost:3000/api/v1/'
		}
		// Test (Vercel preview/deploy) hosts
		if (/\.vercel\.app$/i.test(host)) {
			return 'https://test.api.caremallonline.com/api/v1'
		}
	}
	// Live
	return 'https://api.caremall.in/api/v1/'
}

export const AGENT_API = resolveApiBase()

// ----------------------------------------------------------------------
// Razorpay key resolver - returns test key for dev/vercel, live for production
// ----------------------------------------------------------------------
const resolveRazorpayKey = (): string => {
	const fromEnv = import.meta.env.VITE_RAZORPAY_KEY as string | undefined
	if (fromEnv) return fromEnv

	if (typeof window !== 'undefined') {
		const host = window.location.hostname
		// Local dev
		if (host === 'localhost' || host === '127.0.0.1') {
			return 'rzp_test_pPcGfnYMiqjfP9'
		}
		// Test (Vercel preview/deploy) hosts
		if (/\.vercel\.app$/i.test(host)) {
			return 'rzp_test_pPcGfnYMiqjfP9'
		}
	}
	// Live
	return 'rzp_live_SRrBnucRqISb1b'
}

export const RAZORPAY_KEY = resolveRazorpayKey()

const axiosAdmin = axios.create({
  baseURL: AGENT_API,
  withCredentials: true, // send httpOnly refreshToken cookie so /refresh works
});

axiosAdmin.interceptors.request.use(
  (config) => {
    if (
      !config?.url?.includes("/login") &&
      !config?.url?.includes("/register")
    ) {
      const token = useAuthStore.getState().accessToken;

      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    }

    return config;
  },
  (error: any) => Promise.reject(error)
);

// ----------------------------------------------------------------------
// Token refresh (single-flight) — only one refresh runs at a time even if
// multiple requests get a 401 simultaneously.
// ----------------------------------------------------------------------
let isRefreshing = false;
let failedQueue: Array<{
  resolve: (value?: unknown) => void;
  reject: (reason?: unknown) => void;
}> = [];

const processQueue = (error: unknown, token: string | null) => {
  failedQueue.forEach(({ resolve, reject }) => {
    if (error) reject(error);
    else resolve(token);
  });
  failedQueue = [];
};

axiosAdmin.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & {
      _retry?: boolean;
    };

    const status = error.response?.status;
    const url = originalRequest?.url ?? "";

    // Don't refresh for auth endpoints themselves.
    const isAuthCall =
      url.includes("/login") ||
      url.includes("/logout") ||
      url.includes("/refresh-token");

    if (status === 401 && originalRequest && !originalRequest._retry && !isAuthCall) {
      if (isRefreshing) {
        // Wait for the in-flight refresh, then retry with the new token.
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then((token) => {
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return axiosAdmin(originalRequest);
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const { data } = await axiosAdmin.post<{
          accessToken: string;
          user?: any;
        }>("/user/auth/refresh-token");
        const newToken = data.accessToken;

        // Mirror the email-login flow (setUserData(response.data, token)) so the
        // same consumer shapes (user.user / user.name) keep working after restore.
        useAuthStore.getState().setUserData(
          (data.user
            ? { ...data, accessToken: newToken }
            : useAuthStore.getState().user) as any,
          newToken
        );

        processQueue(null, newToken);
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return axiosAdmin(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError, null);
        useAuthStore.getState().clearAuth();
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(
      (error.response && error.response.data) || "Something went wrong"
    );
  }
);

export { axiosAdmin };